/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2025 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Spinner,
  Stack,
  VStack,
  useToast
} from '@chakra-ui/react'
import { availableResourceTypes } from '../../handlers'
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import { validationSchema } from './assign-permissions-form.schemas'
import { assignUserPermissions } from '@/services/organization'
import { useAuth0 } from '@auth0/auth0-react'

const AssignPermissionsForm = ({
  defaultValues = {},
  onClose,
  organizationId,
  reloadItems
}) => {
  const toast = useToast()
  const { getAccessTokenSilently: getAccessToken } = useAuth0()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: joiResolver(validationSchema),
    defaultValues
  })
  const onSubmit = async (data) => {
    try {
      await assignUserPermissions({ organizationId, data }, getAccessToken)

      toast({
        title: 'Success',
        description: 'Assign was successful',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
      onClose()
      reloadItems()
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to assign permissions. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      })
    }
  }

  return (
    isSubmitting
      ? <Flex justifyContent="center">
          <Spinner size="xl" />
        </Flex>
      : <form
          id="assign-permissions-form"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormControl isInvalid={errors.userId} mb="3">
            <FormLabel>User Id</FormLabel>
            <Input
              placeholder="Insert the user id"
              size="md"
              {...register('userId')}
            />
            <FormErrorMessage>
              { errors.userId && errors.userId.message }
            </FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={errors.resourceTypes} mb="3">
            <FormLabel>Resource Types</FormLabel>
            <VStack align="stretch" spacing={2}>
              { availableResourceTypes.map((resource) => (
                <Checkbox
                  key={resource.id}
                  value={resource.id}
                  {...register('resourceType')}
                >
                  { resource.name || resource.id }
                </Checkbox>
              ))}
            </VStack>
            <FormErrorMessage>
              { errors.resourceTypes && errors.resourceTypes.message }
            </FormErrorMessage>
          </FormControl>
          <Stack direction="row" spacing={4}>
            <Button onClick={onClose} variant="outline">
              Cancel
            </Button>
            <Button variant="solid" type="submit">
              Assign
            </Button>
          </Stack>
        </form>
  )
}

export default AssignPermissionsForm
